import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import { Checkbox } from '../../components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { MapPin, Users, Wallet, AlertCircle, Link, Ticket, Plus, X } from 'lucide-react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { cn } from '../../lib/utils';
import CurrencyInput from '../../components/common/CurrencyInput';
import PlaceAutocomplete from '../../components/common/PlaceAutocomplete';
import { MapPreview } from './MapPreview';
import { CATEGORIES } from './constants';

export const ActivityDetailsModal = ({
  open,
  onOpenChange,
  editingItem,
  formData,
  setFormData,
  currentDay,
  isDesktop,
  desktopModalTab,
  setDesktopModalTab,
  selectAllMembers,
  selectedMembers,
  members,
  onToggleAllMembers,
  onToggleMemberSelection,
  tripCurrency,
  formatCurrency,
  onSave,
}) => {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MapPin className="w-5 h-5 text-orange-500" />
            {editingItem ? 'Editar Atividade' : 'Nova Atividade'}
          </DialogTitle>
          <DialogDescription>
            {currentDay && format(currentDay, "EEEE, dd 'de' MMMM", { locale: ptBR })}
          </DialogDescription>
        </DialogHeader>

        {/* Desktop: Show tabs ONLY for new activities (not editing) */}
        {isDesktop && !editingItem && (
          <div className="flex gap-1 border-b border-slate-700 mb-4">
            <button
              onClick={() => setDesktopModalTab('details')}
              className={cn(
                "px-4 py-2 text-sm font-medium rounded-t-lg transition-colors",
                desktopModalTab === 'details' 
                  ? "bg-slate-800 text-white border-b-2 border-orange-500" 
                  : "text-slate-400 hover:text-white"
              )}
            >
              Detalhes
            </button>
            <button
              onClick={() => setDesktopModalTab('members')}
              className={cn(
                "px-4 py-2 text-sm font-medium rounded-t-lg transition-colors",
                desktopModalTab === 'members' 
                  ? "bg-slate-800 text-white border-b-2 border-orange-500" 
                  : "text-slate-400 hover:text-white"
              )}
            >
              <Users className="w-4 h-4 inline mr-1" />
              Membros
            </button>
            {!formData.is_free && formData.cost > 0 && (
              <button
                onClick={() => setDesktopModalTab('payments')}
                className={cn(
                  "px-4 py-2 text-sm font-medium rounded-t-lg transition-colors",
                  desktopModalTab === 'payments' 
                    ? "bg-slate-800 text-white border-b-2 border-orange-500" 
                    : "text-slate-400 hover:text-white"
                )}
              >
                <Wallet className="w-4 h-4 inline mr-1" />
                Pagamentos
              </button>
            )}
          </div>
        )}

        {/* Tab Content: Details (or only content for mobile) */}
        {(!isDesktop || desktopModalTab === 'details') && (
          <div className="space-y-4">
            {/* Title */}
            <div className="space-y-2">
              <Label>Título *</Label>
              <Input
                placeholder="Ex: Almoço no restaurante, Passeio na praia..."
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                data-testid="activity-title"
              />
            </div>

            {/* Category */}
            <div className="space-y-2">
              <Label>Categoria</Label>
              <Select
                value={formData.category}
                onValueChange={(v) => setFormData({ ...formData, category: v })}
              >
                <SelectTrigger data-testid="activity-category">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(CATEGORIES).map(([key, { label, icon: Icon }]) => (
                    <SelectItem key={key} value={key}>
                      <div className="flex items-center gap-2">
                        <Icon className="w-4 h-4" />
                        {label}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Location */}
            <div className="space-y-2">
              <Label>Local</Label>
              <PlaceAutocomplete
                value={formData.location_place}
                onChange={(place) => setFormData({ 
                  ...formData, 
                  location: place?.name || '',
                  location_place: place 
                })}
                placeholder="Buscar endereço ou local..."
                showMap={false}
                data-testid="activity-location"
              />
              
              {/* Map Preview */}
              {formData.location_place?.lat && formData.location_place?.lng && (
                <MapPreview 
                  lat={formData.location_place.lat} 
                  lng={formData.location_place.lng} 
                  name={formData.location_place.name}
                />
              )}
            </div>

            {/* Time */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Horário início</Label>
                <Input
                  type="time"
                  value={formData.start_time}
                  onChange={(e) => setFormData({ ...formData, start_time: e.target.value })}
                  data-testid="activity-start-time"
                />
              </div>
              <div className="space-y-2">
                <Label>Horário fim</Label>
                <Input
                  type="time"
                  value={formData.end_time}
                  onChange={(e) => setFormData({ ...formData, end_time: e.target.value })}
                  data-testid="activity-end-time"
                />
              </div>
            </div>

            {/* Description */}
            <div className="space-y-2">
              <Label>Descrição</Label>
              <Textarea
                placeholder="Detalhes sobre a atividade..."
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows={2}
                data-testid="activity-description"
              />
            </div>

            {/* Cost */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className={editingItem ? "text-slate-500" : ""}>Custo estimado</Label>
                <div className="flex items-center gap-2">
                  <span className={cn("text-sm", editingItem ? "text-slate-600" : "text-slate-400")}>Gratuito</span>
                  <Switch
                    checked={formData.is_free}
                    onCheckedChange={(checked) => setFormData({ ...formData, is_free: checked, cost: checked ? 0 : formData.cost })}
                    disabled={!!editingItem}
                  />
                </div>
              </div>
              {!formData.is_free && (
                <CurrencyInput
                  value={formData.cost}
                  onChange={(value) => setFormData({ ...formData, cost: value })}
                  currency={tripCurrency}
                  placeholder="0,00"
                  disabled={!!editingItem}
                  data-testid="activity-cost"
                />
              )}
              {/* Alert message when editing */}
              {editingItem && (
                <div className="flex items-start gap-2 bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-3 mt-2">
                  <AlertCircle className="w-4 h-4 text-yellow-500 mt-0.5 shrink-0" />
                  <p className="text-xs text-yellow-200">
                    Para editar o valor ou marcar como gratuito, utilize o <span className="font-semibold">modal de pagamentos</span> (ícone <Wallet className="w-3 h-3 inline mx-0.5" /> no card da atividade).
                  </p>
                </div>
              )}
            </div>

            {/* Notes */}
            <div className="space-y-2">
              <Label>Observações</Label>
              <Textarea
                placeholder="Notas adicionais..."
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                rows={2}
                data-testid="activity-notes"
              />
            </div>

            {/* Purchase Links */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className="flex items-center gap-2">
                  <Link className="w-4 h-4 text-blue-400" />
                  Links úteis
                </Label>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => setFormData({ 
                    ...formData, 
                    purchase_links: [...(formData.purchase_links || []), ''] 
                  })}
                  className="h-7 text-xs text-blue-400 hover:text-blue-300"
                >
                  <Plus className="w-3 h-3 mr-1" />
                  Adicionar link
                </Button>
              </div>
              
              {formData.purchase_links?.length > 0 && (
                <div className="space-y-2">
                  {formData.purchase_links.map((link, idx) => (
                    <div key={idx} className="flex items-center gap-2">
                      <Input
                        placeholder="https://exemplo.com"
                        value={link}
                        onChange={(e) => {
                          const newLinks = [...formData.purchase_links];
                          newLinks[idx] = e.target.value;
                          setFormData({ ...formData, purchase_links: newLinks });
                        }}
                        className="flex-1"
                        data-testid={`activity-link-${idx}`}
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => {
                          const newLinks = formData.purchase_links.filter((_, i) => i !== idx);
                          setFormData({ ...formData, purchase_links: newLinks });
                        }}
                        className="h-8 w-8 text-slate-400 hover:text-red-400"
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
              
              {(!formData.purchase_links || formData.purchase_links.length === 0) && (
                <p className="text-xs text-slate-500">
                  Adicione links de reservas, ingressos ou informações úteis
                </p>
              )}
            </div>

            {/* Tickets Info */}
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Ticket className="w-4 h-4 text-purple-400" />
                Ingressos / Reservas
              </Label>
              <Textarea
                placeholder="Código de reserva, número do ingresso, confirmação..."
                value={formData.tickets_info}
                onChange={(e) => setFormData({ ...formData, tickets_info: e.target.value })}
                rows={2}
                data-testid="activity-tickets-info"
              />
            </div>
          </div>
        )}

        {/* Tab Content: Members (Desktop only, new activities only) */}
        {isDesktop && !editingItem && desktopModalTab === 'members' && (
          <div className="space-y-4">
            <div className="flex items-center justify-between bg-slate-800 rounded-lg p-3">
              <div className="flex items-center gap-2">
                <Users className="w-4 h-4 text-orange-400" />
                <Label className="text-sm">Todos os membros</Label>
              </div>
              <Checkbox
                checked={selectAllMembers}
                onCheckedChange={onToggleAllMembers}
              />
            </div>
            
            <div className="bg-slate-800/50 rounded-lg p-3 space-y-2 max-h-60 overflow-y-auto">
              {members?.map((member) => (
                <label
                  key={member.user_id}
                  className="flex items-center gap-3 p-2 rounded hover:bg-slate-700 cursor-pointer"
                >
                  <Checkbox
                    checked={selectedMembers?.includes(member.user_id)}
                    onCheckedChange={() => onToggleMemberSelection(member.user_id)}
                  />
                  <span className="text-sm text-slate-200">{member.user_name}</span>
                </label>
              ))}
            </div>
            
            <p className="text-xs text-slate-500 text-center">
              {selectAllMembers 
                ? 'Todos os membros da viagem participarão' 
                : `${selectedMembers?.length || 0} membro(s) selecionado(s)`}
            </p>
          </div>
        )}

        {/* Tab Content: Payments (Desktop only, new activities only) */}
        {isDesktop && !editingItem && desktopModalTab === 'payments' && !formData.is_free && formData.cost > 0 && (
          <div className="space-y-4">
            <div className="bg-slate-800 rounded-lg p-4 text-center">
              <p className="text-sm text-slate-400">Valor total da atividade</p>
              <p className="text-2xl font-bold text-white">{formatCurrency(formData.cost)}</p>
              <p className="text-xs text-slate-500 mt-1">
                {selectAllMembers ? members?.length || 0 : selectedMembers?.length || 0} participantes
              </p>
            </div>
            
            <p className="text-sm text-slate-400 text-center">
              Configure o rateio de pagamentos após salvar a atividade.
            </p>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button onClick={onSave} className="bg-orange-500 hover:bg-orange-600">
            {editingItem ? 'Salvar' : 'Adicionar'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
